/*
 * Sidebar link to the Notifications page, with a dot when something is open.
 *
 * "Notifications", not "Alerts". An alert implies something is wrong and demands action; what
 * this page carries is what the fleet noticed, some of which resolves itself and some of which
 * is a suggestion rather than a finding. A label that overstates gets ignored, and an ignored
 * label is the same as no detector at all.
 *
 * The dot is `SidebarItem`'s own `hasNotifications` — a MUI dot badge on the icon, upstream and
 * unstyled by us. A dot rather than a count on purpose: it says *there is something*, and the
 * page says what. A number in the sidebar is a second place for a fact to be wrong, and it would
 * go stale the moment somebody resolved one in another tab.
 *
 * When it does not know — a failed fetch, a guest session, a deployment with notifications off —
 * it shows nothing. `shouldFlagNav` carries the reasoning for why silence is right here and
 * nowhere else in this product.
 */
import { useEffect, useState } from 'react';
import { SidebarItem } from '@backstage/core-components';
import { configApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import NotificationsIcon from '@material-ui/icons/NotificationsNone';

import { ControlPlaneClient } from '../fleet/controlPlane';
import { shouldFlagNav } from './notificationsModel';
import { NOTIFICATIONS_PATH } from './index';

export function NotificationsNavItem() {
  const config = useApi(configApiRef);
  const identity = useApi(identityApiRef);
  const [flag, setFlag] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const controlPlaneUrl =
      config.getOptionalString('stackschematic.controlPlaneUrl') ?? window.location.origin;

    // Once, on mount. The sidebar outlives route changes in an SPA, so this is one request per
    // full page load — not per navigation. Deliberately not polled: a dot that appears while
    // somebody is reading an unrelated page is a notification system of its own, and this is
    // meant to be a signpost.
    void (async () => {
      try {
        const { token } = await identity.getCredentials();
        const client = new ControlPlaneClient({ baseUrl: controlPlaneUrl, hostToken: token });
        // No `all=1`: only what is still open is worth a signpost. Resolved notifications stay
        // on the page for the history they carry, and there is nothing to act on.
        const body = await client.call<{ notifications: unknown[] }>('/admin/notifications');
        if (!cancelled) setFlag(shouldFlagNav({ ok: true, open: body.notifications.length }));
      } catch {
        // Silent. See shouldFlagNav: the nav is a courtesy, and the page is where "could not
        // ask" is said out loud.
        if (!cancelled) setFlag(shouldFlagNav({ ok: false }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [config, identity]);

  return (
    <SidebarItem
      icon={NotificationsIcon}
      to={NOTIFICATIONS_PATH}
      text="Notifications"
      hasNotifications={flag}
    />
  );
}
